import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TabsModule } from 'primeng/tabs';
import { SkeletonModule } from 'primeng/skeleton';

import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EquipamentoService, PostoService } from '@core/services/domain-services';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import {
  Equipamento,
  EquipamentoPropriedadeValor,
  EnumMarcaCategoria,
} from '@core/models/models';
import { DataSourceRegistryService } from '@core/services/data-source-registry.service';
import { environment } from '@environments/environment';
import {
  getPropriedadeValor,
  formatPropriedadeLabel,
  agruparPorTipoDado,
  GrupoPropriedades,
} from '@shared/utils/equipamento.utils';
import { EquipamentoMaintenanceTabComponent } from '../components/equipamento-maintenance-tab/equipamento-maintenance-tab.component';
import { EquipamentoSeguroTabComponent, ApoliceSeguro } from '../components/equipamento-seguro-tab/equipamento-seguro-tab.component';
import { EquipamentoCombustivelTabComponent } from '../components/equipamento-combustivel-tab/equipamento-combustivel-tab.component';
import { EquipamentoInspecaoTabComponent } from '../components/equipamento-inspecao-tab/equipamento-inspecao-tab.component';
import { EquipmentPhotoComponent } from '../components/equipment-photo/equipment-photo.component';

interface Manutencao {
  id: number;
  tipo: string;
  tipoLabel: string;
  data: string;
  tecnico: string;
  descricao: string;
  custo: number;
  icone: string;
  cor: string;
}

interface Documento {
  id?: number;
  nome: string;
  urlFicheiro: string;
  data: string;
  icone: string;
  cor: string;
}

interface EquipmentHighlight {
  label: string;
  value: string;
  icon: string;
  tone: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
  selector: 'app-detalhe-equipamento',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TabsModule,
    SkeletonModule,
    TooltipModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    InputNumberModule,
    TextareaModule,
    BackButtonComponent,
    EquipamentoMaintenanceTabComponent,
    EquipamentoSeguroTabComponent,
    EquipamentoCombustivelTabComponent,
    EquipamentoInspecaoTabComponent,
    EquipmentPhotoComponent,
  ],
  templateUrl: './detalhe-equipamento.component.html',
  styleUrl: './detalhe-equipamento.component.scss',
})
export class DetalheEquipamentoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  private readonly svc = inject(EquipamentoService);
  private readonly postoSvc = inject(PostoService);
  private readonly http = inject(HttpClient);
  private readonly msg = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataSourceRegistry = inject(DataSourceRegistryService);

  equipamento: Equipamento | null = null;
  postoAtribuidoNome = '';
  loading = true;
  equipId = 0;
  today = new Date();
  activeTab: string | number = '0';
  openMaintenanceForm = false;
  private readonly marcaNomes = new Map<string, string>();
  private readonly modeloNomes = new Map<string, string>();

  manutencoes: Manutencao[] = [];
  maintenanceCount = 0;
  inspecaoCount = 0;
  documentos: Documento[] = [];

  showNovaManutencao = false;
  savingManutencao = false;
  editingManutencaoId: number | null = null;
  novaManutencao = {
    tipo: 'REVISAO' as string,
    data: null as Date | null,
    tecnico: '',
    descricao: '',
    custo: null as number | null,
  };

  showNovoDocumento = false;
  savingDocumento = false;
  novoDocumento: { file: File | null; designacao: string } = {
    file: null,
    designacao: '',
  };

  openMenuId: string | null = null;

  @HostListener('document:click') closeMenus(): void {
    this.openMenuId = null;
  }

  readonly tiposManutencao = [
    { label: 'Revisão', value: 'REVISAO' },
    { label: 'Reparação', value: 'REPARACAO' },
    { label: 'Calibração', value: 'CALIBRACAO' },
    { label: 'Inspecção', value: 'INSPECCAO' },
  ];

  ngOnInit(): void {
    this.equipId = Number(this.route.snapshot.paramMap.get('id'));
    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    const requestedAction = this.route.snapshot.queryParamMap.get('acao');
    if (requestedTab === 'manutencao' || requestedTab === 'inspecao') {
      this.activeTab = '1';
    } else if (requestedTab === 'seguro') {
      this.activeTab = '2';
    } else if (requestedTab === 'combustivel') {
      this.activeTab = '3';
    } else if (requestedTab === 'documentos') {
      this.activeTab = '4';
    } else {
      this.activeTab = '0';
    }
    this.openMaintenanceForm = this.activeTab === '1' && requestedAction === 'registar';
    this.load();
  }

  onApoliceChange(novaApolice: ApoliceSeguro): void {
    if (this.equipamento && this.equipamento.propriedades) {
      const idx = this.equipamento.propriedades.findIndex(
        (p) => p.nome?.toUpperCase() === 'VALIDADE_SEGURO',
      );
      if (idx >= 0) {
        this.equipamento.propriedades[idx].valor = novaApolice.dataFim;
      }
      const idxSeg = this.equipamento.propriedades.findIndex(
        (p) => p.nome?.toUpperCase() === 'SEGURADORA',
      );
      if (idxSeg >= 0) {
        this.equipamento.propriedades[idxSeg].valor = novaApolice.seguradora;
      }
      this.equipamento = { ...this.equipamento };
      this.load();
    }
  }

  onCombustivelChange(event: { registo: any; novoKm: number }): void {
    if (this.equipamento && this.equipamento.propriedades) {
      const idxKm = this.equipamento.propriedades.findIndex(
        (p) =>
          p.nome?.toUpperCase() === 'QUILOMETRAGEM' ||
          p.nome?.toUpperCase() === 'KM_ATUAL',
      );
      if (idxKm >= 0) {
        this.equipamento.propriedades[idxKm].valor = String(event.novoKm);
      }
      this.equipamento = { ...this.equipamento };
      this.load();
    }
  }

  onInspecaoCountChange(count: number): void {
    this.inspecaoCount = count;
  }

  load(): void {
    this.loading = true;
    this.svc
      .getById(this.equipId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          this.equipamento = data;
          this.carregarPostoAtribuido(data);
          this.carregarNomesMarcaModelo();
          this.manutencoes = (data.manutencoes ?? []).map((m: any) => ({
            id: m.id,
            tipo: m.tipo,
            tipoLabel: this.getTipoLabel(m.tipo),
            data: m.data ?? '',
            tecnico: m.tecnico ?? '',
            descricao: m.descricao ?? '',
            custo: m.custo ?? 0,
            ...this.getManutencaoMeta(m.tipo),
          }));
          this.maintenanceCount = this.manutencoes.length;
          this.documentos = (data.documentos ?? []).map((d: any) => ({
            id: d.id,
            nome: d.designacao || d.nomeArquivo || 'Documento',
            urlFicheiro: d.urlFicheiro ?? '',
            data: d.dataCriacaoFormatada ?? '',
            ...this.getDocIcon(d.nomeArquivo || d.designacao || ''),
          }));
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Equipamento não encontrado.',
            life: 3000,
          });
          this.loading = false;
          this.cdr.detectChanges();
          this.router.navigate(['/admin/equipamentos']);
        },
      });
  }

  private carregarPostoAtribuido(equipamento: any): void {
    if (!equipamento) {
      this.postoAtribuidoNome = '';
      return;
    }

    const posto = equipamento?.posto ?? equipamento?.Posto;
    this.postoAtribuidoNome =
      (posto?.nome && String(posto.nome).trim()) ||
      (posto?.Nome && String(posto.Nome).trim()) ||
      posto?.nomePosto ||
      posto?.NomePosto ||
      posto?.designacao ||
      posto?.Designacao ||
      equipamento?.postoNome ||
      equipamento?.nomePosto ||
      equipamento?.PostoNome ||
      '';

    const propPosto = (equipamento?.propriedades as any[])?.find((p: any) => {
      const nome = String(p.nome ?? '').toUpperCase();
      return nome === 'POSTO_ATRIBUIDO' || nome === 'POSTO';
    });
    const postoDescricao = String(propPosto?.valorDescricao ?? '').trim();
    if (postoDescricao && Number.isNaN(Number(postoDescricao))) {
      this.postoAtribuidoNome = postoDescricao;
    }

    const rawPostoId =
      equipamento?.idPosto ??
      equipamento?.IdPosto ??
      equipamento?.postoId ??
      equipamento?.PostoId ??
      (posto?.id > 0 ? posto?.id : null) ??
      (posto?.Id > 0 ? posto?.Id : null) ??
      propPosto?.valor;

    const postoId = Number(rawPostoId);

    if (!Number.isFinite(postoId) || postoId <= 0) {
      this.cdr.markForCheck();
      return;
    }

    // Procura o posto diretamente via serviço caso o nome ainda não tenha sido mapeado
    this.postoSvc
      .getById(postoId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res: any) => {
        const data = res?.data ?? res;
        if (data) {
          this.postoAtribuidoNome =
            data?.nome ??
            data?.Nome ??
            data?.nomePosto ??
            data?.NomePosto ??
            data?.designacao ??
            data?.Designacao ??
            this.postoAtribuidoNome;
        }
        this.cdr.markForCheck();
      });
  }

  onMaintenanceCountChange(count: number): void {
    this.maintenanceCount = count;
  }

  // ── Manutenção ──────────────────────────────────────────────────────────────

  salvarManutencao(): void {
    if (!this.novaManutencao.descricao && !this.novaManutencao.tecnico) return;
    this.savingManutencao = true;
    const payload = {
      tipoManutencaoEquipamentoEnum: this.novaManutencao.tipo,
      data: this.novaManutencao.data
        ? new Date(this.novaManutencao.data).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      tecResponsavel: this.novaManutencao.tecnico,
      custo: this.novaManutencao.custo ?? 0,
      descricao: this.novaManutencao.descricao,
    };
    const req$ = this.editingManutencaoId
      ? this.http.put<any>(
          `${environment.apiurl}/api/manutencoes-equipamento/${this.editingManutencaoId}`,
          payload,
        )
      : this.http.put<any>(
          `${environment.apiurl}/api/Equipamentos/${this.equipId}/AdicionarManutencao`,
          payload,
        );

    req$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const saved = res?.data ?? res;
        if (this.editingManutencaoId) {
          const idx = this.manutencoes.findIndex(
            (x) => x.id === this.editingManutencaoId,
          );
          if (idx >= 0) {
            this.manutencoes[idx] = {
              ...this.manutencoes[idx],
              tipo: this.novaManutencao.tipo,
              tipoLabel: this.getTipoLabel(this.novaManutencao.tipo),
              data: payload.data,
              tecnico: payload.tecResponsavel,
              descricao: payload.descricao,
              custo: payload.custo,
              ...this.getManutencaoMeta(this.novaManutencao.tipo),
            };
            this.manutencoes = [...this.manutencoes];
          }
        } else {
          const tipoStr = saved?.tipo ?? this.novaManutencao.tipo;
          this.manutencoes.unshift({
            id: saved?.id ?? Date.now(),
            tipo: tipoStr,
            tipoLabel: this.getTipoLabel(tipoStr),
            data: saved?.data ?? payload.data,
            tecnico: saved?.tecnico ?? payload.tecResponsavel,
            descricao: saved?.descricao ?? payload.descricao,
            custo: saved?.custo ?? payload.custo,
            ...this.getManutencaoMeta(tipoStr),
          });
        }
        this.msg.add({
          severity: 'success',
          summary: 'Guardado',
          detail: this.editingManutencaoId
            ? 'Manutenção actualizada.'
            : 'Manutenção registada.',
          life: 3000,
        });
        this.cancelarManutencao();
      },
      error: (err: any) => {
        this.savingManutencao = false;
        this.msg.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.error?.message || 'Falha ao guardar.',
          life: 4000,
        });
      },
    });
  }

  cancelarManutencao(): void {
    this.novaManutencao = {
      tipo: 'REVISAO',
      data: null,
      tecnico: '',
      descricao: '',
      custo: null,
    };
    this.showNovaManutencao = false;
    this.savingManutencao = false;
    this.editingManutencaoId = null;
  }

  toggleMenu(id: string, $event: Event): void {
    $event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  editarManutencao(m: Manutencao): void {
    this.editingManutencaoId = m.id;
    this.novaManutencao = {
      tipo: m.tipo,
      data: m.data ? new Date(m.data) : null,
      tecnico: m.tecnico,
      descricao: m.descricao,
      custo: m.custo,
    };
    this.showNovaManutencao = true;
  }

  eliminarManutencao(m: Manutencao): void {
    if (!confirm(`Eliminar manutenção "${m.tipoLabel}"?`)) return;
    this.http
      .delete<any>(`${environment.apiurl}/api/manutencoes-equipamento/${m.id}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.manutencoes = this.manutencoes.filter((x) => x.id !== m.id);
          this.msg.add({
            severity: 'success',
            summary: 'Eliminado',
            detail: 'Manutenção removida.',
            life: 3000,
          });
        },
        error: (err: any) =>
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Falha ao eliminar.',
            life: 4000,
          }),
      });
  }

  eliminarDocumento(d: Documento): void {
    if (!d.id) return;

    this.http
      .delete<any>(
        `${environment.apiurl}/api/Equipamentos/${d.id}/EliminarDocumento`,
        {}
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.documentos = this.documentos.filter((x) => x.id !== d.id);
          this.msg.add({
            severity: 'success',
            summary: 'Eliminado',
            detail: 'Documento removido com sucesso.',
            life: 3000,
          });
        },
        error: (err: any) =>
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Falha ao eliminar o documento.',
            life: 4000,
          }),
      });
  }

  // ── Documento ───────────────────────────────────────────────────────────────

  onDocFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 20 * 1024 * 1024) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Ficheiro muito grande. Máx 20MB.',
        life: 3000,
      });
      return;
    }
    this.novoDocumento.file = file;
    if (!this.novoDocumento.designacao) {
      this.novoDocumento.designacao = file.name.replace(/\.[^.]+$/, '');
    }
  }

  salvarDocumento(): void {
    if (!this.novoDocumento.file) return;
    this.savingDocumento = true;
    const fd = new FormData();
    fd.append(
      'Descricao',
      this.novoDocumento.designacao || this.novoDocumento.file.name,
    );
    fd.append(
      'Ficheiro',
      this.novoDocumento.file,
      this.novoDocumento.file.name,
    );
    this.http
      .put<any>(
        `${environment.apiurl}/api/Equipamentos/${this.equipId}/AdicionarDocumento`,
        fd,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const saved = res?.data ?? res;
          const { icone, cor } = this.getDocIcon(this.novoDocumento.file!.name);
          this.documentos.unshift({
            id: saved?.id,
            nome:
              this.novoDocumento.designacao || this.novoDocumento.file!.name,
            urlFicheiro: saved?.urlFicheiro ?? '',
            data: new Date().toLocaleDateString('pt-AO'),
            icone,
            cor,
          });
          this.msg.add({
            severity: 'success',
            summary: 'Guardado',
            detail: 'Documento adicionado.',
            life: 3000,
          });
          this.cancelarDocumento();
        },
        error: (err: any) => {
          this.savingDocumento = false;
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Falha ao carregar.',
            life: 4000,
          });
        },
      });
  }

  cancelarDocumento(): void {
    this.novoDocumento = { file: null, designacao: '' };
    this.showNovoDocumento = false;
    this.savingDocumento = false;
  }

  downloadDocumento(url: string, nome: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.download = nome;
    a.click();
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  private getManutencaoMeta(tipo: string | number): {
    icone: string;
    cor: string;
  } {
    const map: Record<string, { icone: string; cor: string }> = {
      REVISAO: { icone: 'ri-tools-line', cor: 'mnt-revisao' },
      REPARACAO: { icone: 'ri-wrench-line', cor: 'mnt-reparacao' },
      CALIBRACAO: { icone: 'ri-settings-4-line', cor: 'mnt-calibracao' },
      INSPECCAO: { icone: 'ri-search-eye-line', cor: 'mnt-inspecao' },
      '0': { icone: 'ri-tools-line', cor: 'mnt-revisao' },
      '1': { icone: 'ri-wrench-line', cor: 'mnt-reparacao' },
      '2': { icone: 'ri-settings-4-line', cor: 'mnt-calibracao' },
      '3': { icone: 'ri-search-eye-line', cor: 'mnt-inspecao' },
    };
    return map[String(tipo)] ?? { icone: 'ri-tools-line', cor: 'mnt-revisao' };
  }

  private getTipoLabel(tipo: string | number): string {
    const labels: Record<string, string> = {
      REVISAO: 'Revisão',
      REPARACAO: 'Reparação',
      CALIBRACAO: 'Calibração',
      INSPECCAO: 'Inspecção',
      '0': 'Revisão',
      '1': 'Reparação',
      '2': 'Calibração',
      '3': 'Inspecção',
    };
    return labels[String(tipo)] ?? String(tipo);
  }

  private getDocIcon(fileName: string): { icone: string; cor: string } {
    const ext = (fileName.split('.').pop() ?? '').toLowerCase();
    if (ext === 'pdf')
      return { icone: 'ri-file-pdf-line', cor: 'doc-relatorio' };
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
      return { icone: 'ri-image-line', cor: 'doc-registo' };
    if (['doc', 'docx'].includes(ext))
      return { icone: 'ri-file-word-line', cor: 'doc-manual' };
    if (['xls', 'xlsx'].includes(ext))
      return { icone: 'ri-file-excel-line', cor: 'doc-seguro' };
    return { icone: 'ri-file-line', cor: 'doc-manual' };
  }

  // ── Propriedades EAV ────────────────────────────────────────────────────────

  getPropriedadeValor(nome: string): string | null {
    return getPropriedadeValor(this.equipamento, nome);
  }

  get marcaEquipamento(): string | null {
    const raw = this.getPropriedadeDetalhe('MARCA') ??
      this.valorPrincipal('marca', 'Marca', 'marcaNome', 'MarcaNome', 'nomeMarca', 'NomeMarca');
    return raw ? this.marcaNomes.get(String(raw)) ?? raw : null;
  }

  get modeloEquipamento(): string | null {
    const raw = this.getPropriedadeDetalhe('MODELO') ??
      this.valorPrincipal('modelo', 'Modelo', 'modeloNome', 'ModeloNome', 'nomeModelo', 'NomeModelo');
    return raw ? this.modeloNomes.get(String(raw)) ?? raw : null;
  }

  private getPropriedadeDetalhe(nome: string): string | null {
    const equipamento = this.equipamento as any;
    const propriedades = equipamento?.propriedades ?? equipamento?.Propriedades ?? [];
    const prop = propriedades.find((item: any) =>
      String(item?.nome ?? item?.Nome ?? '').toUpperCase() === nome,
    );
    const value = prop?.valorDescricao ?? prop?.ValorDescricao ?? prop?.valor ?? prop?.Valor;
    return value === null || value === undefined || value === '' ? null : String(value);
  }

  private valorPrincipal(...keys: string[]): string | null {
    const equipamento = this.equipamento as any;
    for (const key of keys) {
      const value = equipamento?.[key];
      const normalized = value?.nome ?? value?.Nome ?? value?.designacao ?? value?.Designacao ?? value;
      if (normalized !== null && normalized !== undefined && normalized !== '') {
        return String(normalized);
      }
    }
    return null;
  }

  private get propriedadesViatura(): EquipamentoPropriedadeValor[] {
    return this.equipamento?.propriedades ?? [];
  }

  private propriedadeNome(prop: EquipamentoPropriedadeValor): string {
    return prop.nome?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase() ?? '';
  }

  private propriedadesPorGrupo(grupo: 'operacional' | 'veiculo' | 'administrativo'): EquipamentoPropriedadeValor[] {
    const operacionais = /ESTADO|KM|QUILOMETR|GPS|MOTORISTA|CONDUTOR|RESPONSAVEL|POSTO|REVISAO|MANUTENCAO|SEGURO|APOLICE|OFICINA|VERIFICACAO/;
    const administrativos = /MATRICULA|AQUISICAO|VALOR|CODIGO|SIGLA|CUSTO|FRENTE|DOCUMENTO|LICENCA/;
    return this.propriedadesViatura.filter((prop) => {
      const nome = this.propriedadeNome(prop);
      // Os campos já apresentados directamente nos cards não se repetem.
      const apresentados = /^(ESTADO|KM_ATUAL|QUILOMETRAGEM|KILOMETRAGEM|KM|KM_INICIAL|KM_ULTIMA_REVISAO|POSTO_ATRIBUIDO|POSTO|MOTORISTA_RESPONSAVEL|MOTORISTA|CONDUTOR|RESPONSAVEL|TEM_GPS|GPS|ESTADO_GPS|DISPOSITIVO_GPS|VALIDADE_SEGURO|SEGURO|PROXIMA_REVISAO_DATA|PROXIMA_REVISAO|PROXIMA_MANUTENCAO_DATA|MARCA|MODELO|COR|ANO_FABRICO|NUMERO_CHASSI|CHASSI|NUMERO_MOTOR|MOTOR|TIPO_COMBUSTIVEL|MATRICULA)$/;
      if (apresentados.test(nome)) return false;
      if (grupo === 'operacional') return operacionais.test(nome);
      if (grupo === 'administrativo') return administrativos.test(nome);
      return !operacionais.test(nome) && !administrativos.test(nome);
    });
  }

  get propriedadesOperacionaisViatura(): EquipamentoPropriedadeValor[] {
    return this.propriedadesPorGrupo('operacional');
  }

  get propriedadesVeiculoViatura(): EquipamentoPropriedadeValor[] {
    return this.propriedadesPorGrupo('veiculo');
  }

  get propriedadesAdministrativasViatura(): EquipamentoPropriedadeValor[] {
    return this.propriedadesPorGrupo('administrativo');
  }

  readonly formatPropriedadeLabel = formatPropriedadeLabel;

  get numeroSerieEquipamento(): string | null {
    if (!this.equipamento) return null;
    return (
      getPropriedadeValor(this.equipamento, 'NUMERO_SERIE') ??
      getPropriedadeValor(this.equipamento, 'MATRICULA') ??
      null
    );
  }

  get equipamentoDisplayName(): string {
    if (!this.equipamento) return 'A carregar...';

    const matricula = this.getPropriedadeValor('MATRICULA');
    const tipoArma = this.getPropriedadeValor('TIPO');
    const marca = this.getPropriedadeValor('MARCA') ?? this.equipamento.marca;
    const modelo = this.getPropriedadeValor('MODELO') ?? this.equipamento.modelo;
    const ano = this.getPropriedadeValor('ANO') ?? this.getPropriedadeValor('ANO_FABRICO') ?? this.getPropriedadeValor('ANO FABRICO');
    
    const marcaModelo = [marca, modelo].filter(Boolean).join(' ');

    if (this.isViatura) {
      const marcaModeloAno = [marca, modelo, ano].filter(Boolean).join(' ');
      if (marcaModeloAno) return marcaModeloAno;
      if (matricula) return 'Viatura ' + matricula;
    }

    if (this.isArma) {
      const nomeArma = [tipoArma, marcaModelo].filter(Boolean).join(' · ');
      if (nomeArma) return nomeArma;
    }

    return (
      marcaModelo ||
      this.equipamento.tipoEquipamento?.designacao?.trim() ||
      'Detalhe do equipamento'
    );
  }

  get matriculaEquipamento(): string | null {
    return (
      this.getPropriedadeValor('MATRICULA') ??
      this.getPropriedadeValor('PLACA')
    );
  }

  get uaCCustosEquipamento(): string | null {
    return this.getPropriedadeValor('UA_C_CUSTOS');
  }

  get corEquipamento(): string | null {
    return this.getPropriedadeValor('COR');
  }

  get anoFabricoEquipamento(): string | null {
    return (
      this.getPropriedadeValor('ANO_FABRICO') ??
      this.getPropriedadeValor('ANO')
    );
  }

  get chassiEquipamento(): string | null {
    return (
      this.getPropriedadeValor('NUMERO_CHASSI') ??
      this.getPropriedadeValor('CHASSI')
    );
  }

  get numeroMotorEquipamento(): string | null {
    return (
      this.getPropriedadeValor('NUMERO_MOTOR') ??
      this.getPropriedadeValor('MOTOR')
    );
  }

  get kmInicialEquipamento(): string | null {
    return (
      this.getPropriedadeValor('KM_INICIAL') ??
      this.getPropriedadeValor('KM_INICIO')
    );
  }

  get tipoCombustivelEquipamento(): string | null {
    if (!this.equipamento?.id) return null;
    try {
      const key = `sp_combustivel_equip_${this.equipamento.id}`;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const registos = JSON.parse(raw) as Array<{ tipoCombustivel?: string }>;
      const maisRecente = registos[0]?.tipoCombustivel;
      return maisRecente && maisRecente.trim() ? maisRecente : null;
    } catch {
      return null;
    }
  }

  get viaturaTitulo(): string {
    if (!this.equipamento) return '';
    const marca = this.marcaNome;
    const modelo = this.modeloNome;
    const ano = this.getPropriedadeValor('ANO_FABRICO');
    return [marca, modelo, ano].filter(Boolean).join(' • ');
  }

  get marcaNome(): string | null {
    const raw = this.getPropriedadeValor('MARCA') ?? this.equipamento?.marca;
    if (!raw) return null;
    return this.marcaNomes.get(String(raw)) ?? raw;
  }

  get modeloNome(): string | null {
    const raw = this.getPropriedadeValor('MODELO') ?? this.equipamento?.modelo;
    if (!raw) return null;
    return this.modeloNomes.get(String(raw)) ?? raw;
  }

  private carregarNomesMarcaModelo(): void {
    if (!this.isViatura && !this.isUtensilio) return;

    const marcaRaw =
      this.getPropriedadeValor('MARCA') ?? this.equipamento?.marca;
    const modeloRaw =
      this.getPropriedadeValor('MODELO') ?? this.equipamento?.modelo;

    if (marcaRaw && /^\d+$/.test(String(marcaRaw))) {
      const categoria = this.isUtensilio
        ? EnumMarcaCategoria.UTENSILIO
        : EnumMarcaCategoria.VEICULO;
      const sourceKey = this.isUtensilio ? 'MARCAS_UTENSILIO' : 'MARCAS_VEICULO';
      this.dataSourceRegistry
        .resolve(sourceKey, categoria, true)
        .pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
        .subscribe((marcas) => {
          marcas.forEach((marca) =>
            this.marcaNomes.set(String(marca.value), marca.label),
          );
          const marcaId = String(marcaRaw);
          if (modeloRaw && /^\d+$/.test(String(modeloRaw))) {
            this.dataSourceRegistry
              .resolve('MODELOS_POR_MARCA', marcaId, true)
              .pipe(
                catchError(() => of([])),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe((modelos) => {
                modelos.forEach((modelo) =>
                  this.modeloNomes.set(String(modelo.value), modelo.label),
                );
                this.cdr.detectChanges();
              });
          }
          this.cdr.detectChanges();
        });
    }
  }

  get estadoViaturaDireto(): string | null {
    const estadoViatura = this.getPropriedadeValor('ESTADO_VIATURA');
    if (estadoViatura) return estadoViatura;

    const estado = this.getPropriedadeValor('ESTADO');
    if (estado) return estado;

    if (this.equipamento?.ativo === false) return 'Inactivo';

    const estadoGen = String(this.equipamento?.estado ?? '').toUpperCase();
    if (estadoGen === 'BOM' || estadoGen === '1') return 'Operacional';
    if (estadoGen === 'INATIVO' || estadoGen === '3') return 'Inactivo';
    if (estadoGen === 'DESTRUIDO' || estadoGen === '2') return 'Descontinuado';

    return null;
  }

  get kmAtualEquipamento(): string | null {
    if (!this.equipamento) return null;
    const val =
      this.getPropriedadeValor('KM_ATUAL') ??
      this.getPropriedadeValor('QUILOMETRAGEM') ??
      this.getPropriedadeValor('KILOMETRAGEM') ??
      this.getPropriedadeValor('KM') ??
      this.getPropriedadeValor('KM_INICIAL') ??
      this.getPropriedadeValor('KM_ULTIMA_REVISAO') ??
      (this.equipamento as any)?.quilometragem ??
      null;

    if (val === null || val === undefined || val === '') return null;
    const num = Number(String(val).replace(/[^0-9.]/g, ''));
    if (!isNaN(num) && num > 0) {
      return `${new Intl.NumberFormat('pt-AO').format(num)} km`;
    }
    return String(val).includes('km') ? String(val) : `${val} km`;
  }

  get motoristaEquipamento(): string | null {
    if (!this.equipamento) return null;
    return (
      this.getPropriedadeValor('MOTORISTA') ??
      this.getPropriedadeValor('MOTORISTA_RESPONSAVEL') ??
      this.getPropriedadeValor('CONDUTOR') ??
      this.getPropriedadeValor('RESPONSAVEL') ??
      this.getPropriedadeValor('MOTORISTA_ATRIBUIDO') ??
      (this.equipamento as any)?.motorista?.nome ??
      (this.equipamento as any)?.motorista ??
      null
    );
  }

  get gpsEstadoEquipamento(): boolean | null {
    if (!this.equipamento) return null;
    const raw =
      this.getPropriedadeValor('TEM_GPS') ??
      this.getPropriedadeValor('DISPOSITIVO_GPS') ??
      this.getPropriedadeValor('GPS') ??
      this.getPropriedadeValor('ESTADO_GPS');
    if (raw === null || raw === undefined || raw === '') return null;
    const str = String(raw).trim().toLowerCase();
    if (!str) return null;
    return ['true', '1', 'sim', 'yes', 'on', 'online', 'activo', 'ativo', 'ligado'].includes(str);
  }

  get proximaManutencaoData(): string | null {
    return (
      this.getPropriedadeValor('PROXIMA_REVISAO_DATA') ??
      this.getPropriedadeValor('PROXIMA_REVISAO') ??
      this.getPropriedadeValor('PROXIMA_MANUTENCAO_DATA') ??
      null
    );
  }

  get seguroSituacao(): { label: string; tone: 'ok' | 'error' } | null {
    const validade =
      this.getPropriedadeValor('VALIDADE_SEGURO') ??
      this.getPropriedadeValor('SEGURO');
    if (!validade) return null;
    const expirado = new Date(validade).getTime() < Date.now();
    return expirado
      ? { label: 'Seguro expirado', tone: 'error' }
      : { label: 'Seguro válido', tone: 'ok' };
  }

  getEstadoViaturaClass(estado?: string | null): string {
    const val = String(estado ?? '').trim().toLowerCase();
    const classes: Record<string, string> = {
      operacional: 'ev-operacional',
      'em reparação': 'ev-reparacao',
      'em reparacao': 'ev-reparacao',
      avariado: 'ev-avariado',
      sinistrado: 'ev-sinistrado',
      apreendido: 'ev-apreendido',
      descontinuado: 'ev-descontinuado',
    };
    return classes[val] ?? '';
  }

  get equipamentoHighlights(): EquipmentHighlight[] {
    const definitions: Array<{
      label: string;
      property: string;
      icon: string;
      kind?: 'text' | 'date' | 'boolean';
    }> = this.isViatura
      ? [
          { label: 'Matrícula', property: 'MATRICULA', icon: 'ri-car-line' },
          { label: 'Ano de fabrico', property: 'ANO_FABRICO', icon: 'ri-calendar-2-line' },
          { label: 'Cor', property: 'COR', icon: 'ri-palette-line' },
          { label: 'GPS', property: 'TEM_GPS', icon: 'ri-map-pin-user-line', kind: 'boolean' },
          { label: 'Seguro válido até', property: 'VALIDADE_SEGURO', icon: 'ri-shield-check-line', kind: 'date' },
          { label: 'Próxima revisão', property: 'PROXIMA_REVISAO_DATA', icon: 'ri-tools-line', kind: 'date' },
        ]
      : this.isArma
        ? [
            { label: 'Tipo de arma', property: 'TIPO', icon: 'ri-shield-line' },
            { label: 'Calibre', property: 'CALIBRE', icon: 'ri-focus-3-line' },
            { label: 'Nº de série', property: 'NUMERO_SERIE', icon: 'ri-barcode-line' },
            { label: 'País de origem', property: 'PAIS_ORIGEM', icon: 'ri-global-line' },
            { label: 'Licença válida até', property: 'VALIDADE_LICENCA', icon: 'ri-file-shield-2-line', kind: 'date' },
            { label: 'Próxima verificação', property: 'PROXIMA_VERIFICACAO', icon: 'ri-search-eye-line', kind: 'date' },
          ]
        : [
            { label: 'Nº de série', property: 'NUMERO_SERIE', icon: 'ri-barcode-line' },
            { label: 'Marca', property: 'MARCA', icon: 'ri-building-3-line' },
            { label: 'Modelo', property: 'MODELO', icon: 'ri-price-tag-3-line' },
          ];

    return definitions
      .map((definition) => this.toHighlight(definition))
      .filter((highlight): highlight is EquipmentHighlight => highlight !== null);
  }

  private get normalizedEquipmentType(): string {
    return (
      this.equipamento?.tipoEquipamento?.designacao ??
      this.equipamento?.categoria?.designacao ??
      ''
    )
      .trim()
      .toLowerCase();
  }

  get isViatura(): boolean {
    return (
      this.equipamento?.idTipoEquipamento === 2 ||
      this.normalizedEquipmentType.includes('viatur')
    );
  }

  get isUtensilio(): boolean {
    return (
      this.equipamento?.idTipoEquipamento === 3 ||
      this.normalizedEquipmentType.includes('utensil')
    );
  }

  get isArma(): boolean {
    return (
      this.equipamento?.idTipoEquipamento === 1 ||
      this.normalizedEquipmentType.includes('arma')
    );
  }

  private toHighlight(definition: {
    label: string;
    property: string;
    icon: string;
    kind?: 'text' | 'date' | 'boolean';
  }): EquipmentHighlight | null {
    const rawValue = this.getPropriedadeValor(definition.property);
    if (rawValue === null || rawValue === undefined || rawValue === '') return null;

    if (definition.kind === 'boolean') {
      const enabled = ['true', '1', 'sim', 'yes'].includes(
        String(rawValue).trim().toLowerCase(),
      );
      return {
        label: definition.label,
        value: enabled ? 'Sim' : 'Não',
        icon: definition.icon,
        tone: enabled ? 'success' : 'default',
      };
    }

    if (definition.kind === 'date') {
      return {
        label: definition.label,
        value: this.formatDate(rawValue),
        icon: definition.icon,
        tone: this.getDateTone(rawValue),
      };
    }

    return {
      label: definition.label,
      value: String(rawValue),
      icon: definition.icon,
      tone: 'default',
    };
  }

  private getDateTone(value: string): EquipmentHighlight['tone'] {
    const date = new Date(value.slice(0, 10) + 'T00:00:00');
    if (Number.isNaN(date.getTime())) return 'default';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((date.getTime() - today.getTime()) / 86400000);
    if (daysUntil < 0) return 'danger';
    if (daysUntil <= 30) return 'warning';
    return 'success';
  }

  get viaturaDadosVeiculo(): EquipamentoPropriedadeValor[] {
    const keys = ['MARCA', 'MODELO', 'ANO', 'COR', 'CHASSI', 'MOTOR', 'COMBUSTIVEL', 'KM', 'SERIE', 'MATRICULA'];
    if (!this.equipamento?.propriedades?.length) return [];
    return this.equipamento.propriedades.filter(p => keys.some(k => p.nome.toUpperCase().includes(k)));
  }

  get viaturaDadosAdministrativos(): EquipamentoPropriedadeValor[] {
    const keys = ['GPS', 'MOTORISTA'];
    if (!this.equipamento?.propriedades?.length) return [];
    return this.equipamento.propriedades.filter(p => keys.some(k => p.nome.toUpperCase().includes(k)));
  }

  get propriedadesAgrupadas(): GrupoPropriedades<EquipamentoPropriedadeValor>[] {
    if (!this.equipamento?.propriedades?.length) return [];
    let props = this.equipamento.propriedades;
    if (this.isViatura) {
       const excludeKeys = [
         'MARCA', 'MODELO', 'ANO', 'COR', 'CHASSI', 'MOTOR', 'COMBUSTIVEL', 'KM', 'SERIE', 'MATRICULA',
         'GPS', 'MOTORISTA',
         'REVISAO', 'OFICINA',
         'SEGUR', 'APOLICE', 'FRANQUIA'
       ];
       props = props.filter(p => !excludeKeys.some(k => p.nome.toUpperCase().includes(k)));
    }
    return agruparPorTipoDado(props);
  }

  formatPropriedadeValor(prop: EquipamentoPropriedadeValor): string {
    const value = prop.valorDescricao ?? prop.valor;
    if (value === null || value === undefined || value === '')
      return '—';
    if (prop.tipoDado === 4) return this.formatDate(String(value));
    if (prop.tipoDado === 2) {
      const n = Number(value);
      return isNaN(n) ? String(value) : new Intl.NumberFormat('pt-AO').format(n);
    }
    return String(value);
  }

  getEstadoClass(estado?: string | number): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'badge-bom';
    if (val === 'DESTRUIDO' || val === '2') return 'badge-destruido';
    if (val === 'INATIVO' || val === '3') return 'badge-inativo';
    return '';
  }

  getEstadoLabel(estado?: string | number): string {
    const val = String(estado ?? '').toUpperCase();
    if (val === 'BOM' || val === '1') return 'Bom Estado';
    if (val === 'DESTRUIDO' || val === '2') return 'Destruído';
    if (val === 'INATIVO' || val === '3') return 'Inactivo';
    return '—';
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('pt-AO');
    } catch {
      return d;
    }
  }

  // ── Arma-specific getters ──────────────────────────────────────────────────

  get tipoArma(): string | null {
    return this.getPropriedadeValor('TIPO');
  }

  get marcaArma(): string | null {
    return this.getPropriedadeValor('MARCA') ?? this.equipamento?.marca ?? null;
  }

  get modeloArma(): string | null {
    return this.getPropriedadeValor('MODELO') ?? this.equipamento?.modelo ?? null;
  }

  get calibreArma(): string | null {
    return this.getPropriedadeValor('CALIBRE');
  }

  get paisOrigemArma(): string | null {
    return this.getPropriedadeValor('PAIS_ORIGEM');
  }

  get capacidadeMunicoesArma(): string | null {
    return this.getPropriedadeValor('CAPACIDADE_MUNICOES');
  }

  get guardaResponsavelArma(): string | null {
    return (
      this.getPropriedadeValor('GUARDA_RESPONSAVEL') ??
      this.getPropriedadeValor('RESPONSAVEL') ??
      this.getPropriedadeValor('MOTORISTA') ??
      null
    );
  }

  get situacaoLicencaArma(): { label: string; tone: 'ok' | 'error' } | null {
    const validade = this.getPropriedadeValor('VALIDADE_LICENCA');
    if (!validade) return null;
    const expirado = new Date(validade).getTime() < Date.now();
    return expirado
      ? { label: 'Licença expirada', tone: 'error' }
      : { label: 'Licença válida', tone: 'ok' };
  }

  get proximaVerificacaoArma(): string | null {
    return this.getPropriedadeValor('PROXIMA_VERIFICACAO_OBRIGATORIA');
  }

  get numeroLicencaArma(): string | null {
    return this.getPropriedadeValor('NUMERO_LICENCA');
  }

  get entidadeEmissoraArma(): string | null {
    return this.getPropriedadeValor('ENTIDADE_EMISSORA');
  }

  get dataEmissaoLicencaArma(): string | null {
    return this.getPropriedadeValor('DATA_EMISSAO_LICENCA');
  }

  get validadeLicencaArma(): string | null {
    return this.getPropriedadeValor('VALIDADE_LICENCA');
  }

  get dataUltimaVerificacaoArma(): string | null {
    return this.getPropriedadeValor('DATA_ULTIMA_VERIFICACAO');
  }

  get frequenciaVerificacaoArma(): string | null {
    return this.getPropriedadeValor('FREQUENCIA_VERIFICACAO');
  }

  // ── Utensilio-specific getters ─────────────────────────────────────────────

  get nomeItemUtensilio(): string | null {
    return (
      this.getPropriedadeValor('NOME_ITEM') ??
      this.getPropriedadeValor('NOME') ??
      this.getPropriedadeValor('DESIGNACAO') ??
      null
    );
  }

  get categoriaUtensilio(): string | null {
    return (
      this.getPropriedadeValor('CATEGORIA') ??
      this.equipamento?.categoria?.designacao?.trim() ??
      null
    );
  }

  get responsavelUtensilio(): string | null {
    return (
      this.getPropriedadeValor('RESPONSAVEL') ??
      this.getPropriedadeValor('GUARDA_RESPONSAVEL') ??
      this.getPropriedadeValor('MOTORISTA') ??
      null
    );
  }

  editarEquipamento(): void {
    this.router.navigate(['/admin/equipamentos', this.equipId, 'editar']);
  }
}
